// DOS-38: shared feedback submission + post-dose micro-prompt throttling.

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

export interface FeedbackPayload {
  message?: string;
  rating?: number;
  email?: string;
  trigger: 'widget' | 'post_dose';
}

export const RATING_OPTIONS = [
  { value: 1, emoji: '😞', label: 'Very poor' },
  { value: 2, emoji: '😕', label: 'Poor' },
  { value: 3, emoji: '😐', label: 'Okay' },
  { value: 4, emoji: '🙂', label: 'Good' },
  { value: 5, emoji: '😍', label: 'Excellent' },
] as const;

// A submission needs a rating or a real message (mirrors backend validation).
export function isSubmittable(rating: number | null, message: string): boolean {
  return rating !== null || message.trim().length >= 3;
}

export function buildFeedbackRequest(
  payload: FeedbackPayload,
  page: string,
  token: string | null
): { headers: Record<string, string>; body: string } {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const body = JSON.stringify({
    message: payload.message?.trim() || undefined,
    rating: payload.rating ?? undefined,
    email: payload.email?.trim() || undefined,
    context: {
      page,
      appVersion: process.env.REACT_APP_VERSION,
      trigger: payload.trigger,
    },
  });
  return { headers, body };
}

export async function submitFeedback(payload: FeedbackPayload): Promise<void> {
  const token = localStorage.getItem('token');
  const page = window.location.pathname;
  const { headers, body } = buildFeedbackRequest(payload, page, token);
  const res = await fetch(`${API_BASE_URL}/feedback`, { method: 'POST', headers, body });
  if (!res.ok) throw new Error('Failed to submit feedback');
}

// Post-dose micro-prompt: at most once every 14 days; dismissing also counts
// as shown so users are never nagged.
export const MICRO_PROMPT_INTERVAL_DAYS = 14;
const MICRO_PROMPT_KEY = 'feedbackMicroPromptLast';

export function shouldShowMicroPrompt(lastShownIso: string | null, now: Date = new Date()): boolean {
  if (!lastShownIso) return true;
  const last = new Date(lastShownIso);
  if (isNaN(last.getTime())) return true;
  return now.getTime() - last.getTime() >= MICRO_PROMPT_INTERVAL_DAYS * 24 * 60 * 60 * 1000;
}

export function microPromptDue(): boolean {
  try {
    return shouldShowMicroPrompt(localStorage.getItem(MICRO_PROMPT_KEY));
  } catch {
    return false;
  }
}

export function markMicroPromptShown(now: Date = new Date()): void {
  try {
    localStorage.setItem(MICRO_PROMPT_KEY, now.toISOString());
  } catch {
    // localStorage unavailable — fail quiet, prompt just won't throttle.
  }
}
